import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const selfAssessmentQuestions = [
  {
    category: 'Facial Condition',
    icon: 'ri-body-scan-line',
    question: 'When you look in the mirror, which of the following feels most familiar?',
    options: [
      { label: 'My face looks tired or tense even after a full night\'s sleep', score: 3 },
      { label: 'I notice subtle asymmetry or uneven tone that wasn\'t there before', score: 3 },
      { label: 'My skin feels reactive — it responds differently week to week', score: 2 },
      { label: 'I\'m generally happy with my skin, just looking for maintenance', score: 0 },
    ],
  },
  {
    category: 'Facial Condition',
    icon: 'ri-lungs-line',
    question: 'How would you describe the area around your jaw and mid-face on most days?',
    options: [
      { label: 'Chronically tight — I often catch myself clenching without realising', score: 3 },
      { label: 'Heavy or dense, like the tissue holds more than it should', score: 3 },
      { label: 'Occasionally tense during stressful periods', score: 2 },
      { label: 'Relaxed and comfortable most of the time', score: 0 },
    ],
  },
  {
    category: 'Internal State',
    icon: 'ri-mental-health-line',
    question: 'How would you describe your internal experience on a typical day?',
    options: [
      { label: 'Wired but composed — I function well, but the tension underneath is constant', score: 3 },
      { label: 'Quietly depleted — I give a lot and rarely feel fully restored', score: 3 },
      { label: 'Occasionally overwhelmed, but I recover fairly quickly', score: 1 },
      { label: 'Generally balanced and regulated', score: 0 },
    ],
  },
  {
    category: 'Internal State',
    icon: 'ri-heart-pulse-line',
    question: 'How does stress tend to show up in your body?',
    options: [
      { label: 'In my face — jaw, forehead, or around the eyes', score: 3 },
      { label: 'As fatigue that doesn\'t fully lift even with rest', score: 3 },
      { label: 'As tension in my neck and shoulders that occasionally reaches my face', score: 2 },
      { label: 'I manage stress well and it rarely affects my appearance', score: 0 },
    ],
  },
  {
    category: 'Timeline & Expectations',
    icon: 'ri-time-line',
    question: 'When you invest in a treatment or wellness practice, what matters most to you?',
    options: [
      { label: 'Long-term, sustainable results — I\'m willing to be patient', score: 3 },
      { label: 'Gradual, visible improvement over a series of sessions', score: 3 },
      { label: 'A noticeable result within the first few sessions', score: 1 },
      { label: 'Immediate visible change — I prefer fast results', score: 0 },
    ],
  },
  {
    category: 'Timeline & Expectations',
    icon: 'ri-calendar-check-line',
    question: 'How do you feel about committing to a structured, ongoing care plan?',
    options: [
      { label: 'This is exactly how I prefer to approach my wellbeing', score: 3 },
      { label: 'I\'m open to it if I understand the reasoning behind the structure', score: 2 },
      { label: 'I tend to invest in self-care during high-stress periods', score: 1 },
      { label: 'I prefer one-off treatments without ongoing commitment', score: 0 },
    ],
  },
  {
    category: 'Consistency & Mindset',
    icon: 'ri-seedling-line',
    question: 'How do you relate to the idea that visible change begins from within?',
    options: [
      { label: 'Deeply — I\'ve always believed the body and face are connected', score: 3 },
      { label: 'I\'m curious and open to exploring this approach', score: 2 },
      { label: 'I\'m somewhat sceptical but willing to try', score: 1 },
      { label: 'I prefer treatments that focus on the surface directly', score: 0 },
    ],
  },
  {
    category: 'Consistency & Mindset',
    icon: 'ri-focus-3-line',
    question: 'Which statement best reflects your relationship with self-care?',
    options: [
      { label: 'I invest consistently — I see it as a long-term responsibility to myself', score: 3 },
      { label: 'I\'m building a more consistent practice and looking for the right anchor', score: 3 },
      { label: 'I tend to invest in self-care during high-stress periods', score: 1 },
      { label: 'I prefer minimal, low-commitment routines', score: 0 },
    ],
  },
];

const totalSaScore = selfAssessmentQuestions.length * 3;

const getSaResult = (score: number) => {
  const pct = score / totalSaScore;
  if (pct >= 0.75) return {
    level: 'Strong Alignment',
    tag: 'This is designed for you.',
    bgClass: 'bg-[#9C8165]',
    textClass: 'text-[#9C8165]',
    barWidth: `${Math.round(pct * 100)}%`,
    description: 'Your facial patterns, internal state, and approach to care align closely with what the Stability Method is designed to address. You are not looking for a quick fix — you are looking for something that lasts. This is where that begins.',
    note: 'Your stability journey begins with the 90-minute Stability Signature session.',
    icon: 'ri-sparkling-2-line',
    gradientFrom: 'from-[#F7F2EC]',
  };
  if (pct >= 0.5) return {
    level: 'Good Alignment',
    tag: 'This may be the right time.',
    bgClass: 'bg-[#A89880]',
    textClass: 'text-[#A89880]',
    barWidth: `${Math.round(pct * 100)}%`,
    description: 'Several aspects of your experience resonate with the Stability Method\'s approach. You may be at a point of transition — where surface-level care no longer feels sufficient. A consultation can help clarify whether this is the right fit.',
    note: 'A complimentary consultation is available to explore your specific needs.',
    icon: 'ri-leaf-line',
    gradientFrom: 'from-[#F5F1EB]',
  };
  return {
    level: 'Exploratory Fit',
    tag: 'Worth exploring further.',
    bgClass: 'bg-[#C4B8A8]',
    textClass: 'text-[#C4B8A8]',
    barWidth: `${Math.round(pct * 100)}%`,
    description: 'Your current priorities may differ from what the Stability Method focuses on. That said, many clients arrive before they feel "ready" — and find that the first session clarifies everything. You are welcome to reach out with questions.',
    note: 'Feel free to reach out — we are happy to answer any questions before you decide.',
    icon: 'ri-question-line',
    gradientFrom: 'from-[#F3EFE9]',
  };
};

const categories = [
  { icon: 'ri-body-scan-line', label: 'Facial Condition', desc: 'What your face is currently experiencing' },
  { icon: 'ri-mental-health-line', label: 'Internal State', desc: 'How your nervous system is responding' },
  { icon: 'ri-time-line', label: 'Timeline & Expectations', desc: 'Your relationship with long-term investment' },
  { icon: 'ri-seedling-line', label: 'Consistency & Mindset', desc: 'Your approach to sustained self-care' },
];

const categoryBreakdown = [
  { label: 'Facial Condition', qs: [0, 1] },
  { label: 'Internal State', qs: [2, 3] },
  { label: 'Timeline & Expectations', qs: [4, 5] },
  { label: 'Consistency & Mindset', qs: [6, 7] },
];

type Step = 'intro' | 'quiz' | 'result';

const ClientSuitability = () => {
  const [step, setStep] = useState<Step>('intro');
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [fitScore, setFitScore] = useState(0);
  const navigate = useNavigate();

  const handleSelect = (idx: number) => setSelected(idx);

  const handleNext = () => {
    if (selected === null) return;
    const score = selfAssessmentQuestions[currentQ].options[selected].score;
    const newAnswers = [...answers, selected];
    setAnswers(newAnswers);
    setFitScore(fitScore + score);
    setSelected(null);
    if (currentQ + 1 < selfAssessmentQuestions.length) {
      setCurrentQ(currentQ + 1);
    } else {
      setStep('result');
    }
  };

  const handleBack = () => {
    if (currentQ === 0) return;
    const prevQ = currentQ - 1;
    const prevAnswer = answers[prevQ];
    const prevScore = selfAssessmentQuestions[prevQ].options[prevAnswer]?.score ?? 0;
    setFitScore(fitScore - prevScore);
    setAnswers(answers.slice(0, prevQ));
    setSelected(prevAnswer ?? null);
    setCurrentQ(prevQ);
  };

  const handleRestart = () => {
    setStep('intro');
    setCurrentQ(0);
    setAnswers([]);
    setSelected(null);
    setFitScore(0);
  };

  const scrollToForm = () => {
    const form = document.getElementById('consultation-form');
    if (form) form.scrollIntoView({ behavior: 'smooth' });
  };

  const res = getSaResult(fitScore);
  const pct = Math.round((fitScore / totalSaScore) * 100);

  return (
    <section className="relative bg-[#F5F3EF] py-12 sm:py-28 overflow-hidden" id="client-suitability">

      {/* Top gradient divider */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-bronze/25 to-transparent"></div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-16">

        {/* Section Header */}
        <div className="mb-8 sm:mb-20">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-8 h-[1px] bg-bronze"></div>
            <span className="text-bronze text-[9px] font-sans font-semibold tracking-[0.35em] uppercase">
              Client Suitability
            </span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-4 lg:gap-20 items-end">
            <h2 className="font-display text-2xl sm:text-4xl lg:text-[3rem] font-light text-charcoal italic leading-[1.15]">
              Is This For Me?
            </h2>
            <p className="hidden sm:block text-charcoal/60 text-[12px] sm:text-[13px] font-sans font-light leading-[1.9]">
              The Stability Method is not for everyone — and that is intentional. It is designed for women who are ready to address the root, not just the surface.
            </p>
          </div>
          <p className="mt-5 text-charcoal/55 text-[12px] sm:text-[13px] font-sans font-light leading-[1.95] max-w-3xl">
            The Stability Method resonates with women who sense the quiet disorder that modern life can bring.
            Over time, this imbalance often becomes visible across the face — in tension, fatigue, or a gradual loss of vitality.
            They understand that beauty and well-being are inseparable and approach care as a long-term investment in balance and sustainability.
          </p>
        </div>

        {/* ── Mobile: compact layout ── */}
        <div className="sm:hidden space-y-4">
          <div className="grid grid-cols-2 gap-x-6 gap-y-2.5">
            {categories.map((cat, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className={`${cat.icon} text-bronze text-[13px]`}></i>
                </div>
                <span className="text-charcoal/75 text-[11px] font-sans font-light leading-snug">{cat.label}</span>
              </div>
            ))}
          </div>
          <button
            onClick={() => setStep('quiz')}
            className="group inline-flex items-center gap-2 px-5 py-2.5 bg-charcoal text-white text-[9px] font-sans font-semibold tracking-[0.28em] uppercase hover:bg-bronze transition-all duration-300 whitespace-nowrap cursor-pointer mt-2"
          >
            <span>Begin Self-Assessment</span>
            <i className="ri-arrow-right-line text-xs group-hover:translate-x-0.5 transition-transform duration-300"></i>
          </button>
        </div>

        {/* ── Desktop: Main Grid ── */}
        <div className="hidden sm:grid grid-cols-1 lg:grid-cols-[1fr_1.7fr] gap-12 lg:gap-20 items-start">

          {/* Left Column (Sticky) */}
          <div className="lg:sticky lg:top-28">
            <p className="text-[9px] font-sans font-semibold tracking-[0.3em] uppercase text-charcoal/30 mb-5">
              Four Focus Areas
            </p>
            <div className="space-y-2 mb-8">
              {categories.map((cat, i) => (
                <div key={i} className="flex items-center gap-3.5 px-4 py-3 bg-white border border-warmGray/15 hover:border-bronze/25 transition-all duration-300">
                  <div className="w-7 h-7 flex items-center justify-center flex-shrink-0 bg-bronze/[0.06]">
                    <i className={`${cat.icon} text-bronze text-xs`}></i>
                  </div>
                  <div className="min-w-0">
                    <p className="text-charcoal text-[10.5px] font-sans font-semibold tracking-wide leading-none mb-0.5">{cat.label}</p>
                    <p className="text-charcoal/40 text-[9.5px] font-sans font-light leading-snug truncate">{cat.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="w-full h-px bg-charcoal/8 mb-8"></div>

            <div className="border-l-[2px] border-bronze/35 pl-5 py-1">
              <p className="font-display text-[1rem] font-light text-charcoal/65 italic leading-[1.7] mb-2">
                "Eight questions. No right or wrong answers."
              </p>
              <p className="text-charcoal/40 text-[10px] font-sans font-light leading-[1.75]">
                Your result will reflect how closely your current experience aligns with what this method addresses.
              </p>
            </div>

            <div className="mt-8 grid grid-cols-3 gap-0 border border-warmGray/20 divide-x divide-warmGray/20 bg-white">
              {[
                { num: '8', label: 'Questions' },
                { num: '~3', label: 'Minutes' },
                { num: '4', label: 'Focus Areas' },
              ].map((s, i) => (
                <div key={i} className="flex flex-col items-center py-5 gap-1">
                  <span className="font-display text-xl font-light text-bronze leading-none">{s.num}</span>
                  <span className="text-[9px] font-sans text-charcoal/30 tracking-wide uppercase">{s.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column: Assessment Panel */}
          <div className="relative">

            {/* INTRO STATE */}
            {step === 'intro' && (
              <div className="relative overflow-hidden border border-warmGray/20 bg-white shadow-sm flex flex-row" style={{ minHeight: '520px' }}>

                {/* Left: Image */}
                <div className="relative w-[45%] flex-shrink-0 overflow-hidden bg-[#F5F3EF]">
                  <img
                    src="https://static.readdy.ai/image/296ab9838c8a2f1e2b2bac03a156c5b2/3fda066cb7ffcf6766995cecd4fec251.png"
                    alt="Client Suitability Assessment"
                    className="w-full h-full object-cover object-[center_15%]"
                  />
                  {/* Floating label */}
                  <div className="absolute top-5 left-5 flex items-center gap-2 px-3 py-1.5 bg-white/90 backdrop-blur-sm border border-warmGray/20 shadow-sm" style={{ zIndex: 4 }}>
                    <div className="w-1.5 h-1.5 rounded-full bg-bronze flex-shrink-0"></div>
                    <span className="text-[8.5px] font-sans font-semibold tracking-[0.25em] uppercase text-charcoal/55 whitespace-nowrap">Find Your Alignment</span>
                  </div>
                </div>

                {/* Right: Content */}
                <div className="flex-1 flex flex-col justify-between px-10 py-10">
                  {/* Top: text */}
                  <div>
                    <p className="text-[9px] font-sans font-semibold tracking-[0.3em] uppercase text-bronze/60 mb-4">
                      Self-Assessment
                    </p>
                    <h3 className="font-display text-[1.6rem] font-light text-charcoal italic leading-[1.3] mb-5">
                      A quiet reflection,<br />your alignment.
                    </h3>
                    <p className="text-charcoal/50 text-[11.5px] font-sans font-light leading-[1.95]">
                      Eight carefully considered questions to help you understand whether the Stability Method speaks to where you are right now.
                    </p>
                  </div>

                  {/* Divider */}
                  <div className="w-full h-px bg-warmGray/20 my-7"></div>

                  {/* Meta info */}
                  <div className="flex items-center gap-6 mb-8">
                    {[
                      { icon: 'ri-question-line', text: '8 Questions' },
                      { icon: 'ri-time-line', text: '~3 Minutes' },
                      { icon: 'ri-focus-3-line', text: '4 Focus Areas' },
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-1.5">
                        <i className={`${item.icon} text-bronze text-xs`}></i>
                        <span className="text-charcoal/45 text-[10px] font-sans font-light">{item.text}</span>
                      </div>
                    ))}
                  </div>

                  {/* Bottom: CTA */}
                  <div>
                    <button
                      onClick={() => setStep('quiz')}
                      className="group w-full flex items-center justify-between px-7 py-4 bg-charcoal text-white hover:bg-bronze transition-all duration-300 cursor-pointer whitespace-nowrap"
                    >
                      <span className="text-[9px] font-sans font-semibold tracking-[0.28em] uppercase">Begin Self-Assessment</span>
                      <i className="ri-arrow-right-line text-xs group-hover:translate-x-0.5 transition-transform duration-300"></i>
                    </button>
                    <p className="text-charcoal/25 text-[9px] font-sans font-light italic mt-3.5">
                      For personal reflection only — not a clinical evaluation.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* QUIZ STATE */}
            {step === 'quiz' && (
              <div className="border border-warmGray/25 bg-white overflow-hidden shadow-sm">

                {/* Progress bar */}
                <div className="h-[3px] bg-warmGray/20 w-full">
                  <div
                    className="h-full bg-bronze transition-all duration-500 ease-out"
                    style={{ width: `${((currentQ + 1) / selfAssessmentQuestions.length) * 100}%` }}
                  ></div>
                </div>

                {/* Step dots */}
                <div className="flex items-center gap-2 px-12 pt-8 pb-0">
                  {selfAssessmentQuestions.map((_, i) => (
                    <div
                      key={i}
                      className={`transition-all duration-300 rounded-full ${
                        i < currentQ
                          ? 'w-5 h-[2.5px] bg-bronze'
                          : i === currentQ
                          ? 'w-5 h-[2.5px] bg-bronze/50'
                          : 'w-2.5 h-[2.5px] bg-warmGray/30'
                      }`}
                    ></div>
                  ))}
                  <span className="ml-auto text-charcoal/30 text-[9px] font-sans font-light tabular-nums">
                    {currentQ + 1}&thinsp;/&thinsp;{selfAssessmentQuestions.length}
                  </span>
                </div>

                {/* Question area */}
                <div className="px-12 pt-10 pb-7">
                  {/* Category tag */}
                  <div className="flex items-center gap-2.5 mb-6">
                    <div className="w-6 h-6 flex items-center justify-center border border-bronze/20 bg-bronze/[0.04]">
                      <i className={`${selfAssessmentQuestions[currentQ].icon} text-bronze text-xs`}></i>
                    </div>
                    <span className="text-[9px] font-sans font-semibold tracking-[0.3em] uppercase text-bronze/70">
                      {selfAssessmentQuestions[currentQ].category}
                    </span>
                  </div>

                  {/* Large question number (decorative) */}
                  <div className="relative">
                    <span className="absolute -top-4 -left-2 font-display text-[5.5rem] font-light text-charcoal/[0.03] leading-none select-none pointer-events-none">
                      {String(currentQ + 1).padStart(2, '0')}
                    </span>
                    <h3 className="relative font-display text-[1.3rem] sm:text-[1.4rem] font-light text-charcoal italic leading-[1.5] pr-6">
                      {selfAssessmentQuestions[currentQ].question}
                    </h3>
                  </div>
                </div>

                {/* Options */}
                <div className="px-12 pb-8 flex flex-col gap-3">
                  {selfAssessmentQuestions[currentQ].options.map((opt, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSelect(idx)}
                      className={`group w-full text-left flex items-start gap-5 px-7 py-5 transition-all duration-200 cursor-pointer border ${
                        selected === idx
                          ? 'border-bronze/50 bg-[#FAF6EC] shadow-sm'
                          : 'border-warmGray/20 bg-white hover:border-warmGray/35 hover:bg-cream/30'
                      }`}
                    >
                      {/* Radio dot */}
                      <div className={`w-[16px] h-[16px] flex-shrink-0 mt-[3px] rounded-full border-[2px] transition-all duration-200 flex items-center justify-center ${
                        selected === idx
                          ? 'border-bronze bg-bronze'
                          : 'border-warmGray/35 group-hover:border-warmGray/50'
                      }`}>
                        {selected === idx && (
                          <div className="w-[5px] h-[5px] rounded-full bg-white"></div>
                        )}
                      </div>
                      <span className={`text-[11.5px] font-sans font-light leading-[1.85] transition-colors duration-200 ${
                        selected === idx ? 'text-charcoal' : 'text-charcoal/55 group-hover:text-charcoal/75'
                      }`}>
                        {opt.label}
                      </span>
                    </button>
                  ))}
                </div>

                {/* Navigation */}
                <div className="flex items-center justify-between px-12 py-6 border-t border-warmGray/15 bg-cream/30">
                  <button
                    onClick={handleBack}
                    className={`flex items-center gap-2.5 text-[9px] font-sans font-semibold tracking-[0.22em] uppercase transition-all duration-200 cursor-pointer ${
                      currentQ === 0
                        ? 'text-charcoal/15 pointer-events-none'
                        : 'text-charcoal/40 hover:text-charcoal'
                    }`}
                  >
                    <i className="ri-arrow-left-line text-xs"></i>
                    <span>Back</span>
                  </button>

                  <button
                    onClick={handleNext}
                    disabled={selected === null}
                    className={`group flex items-center gap-3 px-8 py-3.5 text-[9px] font-sans font-semibold tracking-[0.25em] uppercase transition-all duration-300 whitespace-nowrap cursor-pointer ${
                      selected !== null
                        ? 'bg-charcoal text-white hover:bg-bronze'
                        : 'bg-warmGray/15 text-charcoal/25 cursor-not-allowed'
                    }`}
                  >
                    <span>{currentQ + 1 === selfAssessmentQuestions.length ? 'View Result' : 'Continue'}</span>
                    <i className={`ri-arrow-right-line text-xs transition-transform duration-300 ${selected !== null ? 'group-hover:translate-x-0.5' : ''}`}></i>
                  </button>
                </div>
              </div>
            )}

            {/* RESULT STATE */}
            {step === 'result' && (
              <div className="border border-warmGray/25 bg-white overflow-hidden shadow-sm">

                {/* Result hero */}
                <div className={`relative px-12 pt-12 pb-10 bg-gradient-to-br ${res.gradientFrom} to-white`}>
                  {/* Decorative large number */}
                  <span className="absolute right-10 top-8 font-display text-[8rem] font-light text-charcoal/[0.03] leading-none select-none pointer-events-none">
                    {pct}
                  </span>

                  <div className="flex items-start gap-5 mb-8">
                    <div className="w-11 h-11 flex items-center justify-center border border-bronze/30 bg-white flex-shrink-0">
                      <i className={`${res.icon} text-bronze text-lg`}></i>
                    </div>
                    <div>
                      <p className="text-[9px] font-sans font-semibold tracking-[0.35em] uppercase text-bronze/60 mb-2">
                        Your Assessment Result
                      </p>
                      <h3 className="font-display text-[1.9rem] font-light text-charcoal italic leading-tight">
                        {res.level}
                      </h3>
                      <p className="text-charcoal/45 text-[11.5px] font-sans font-light italic mt-2">
                        {res.tag}
                      </p>
                    </div>
                  </div>

                  {/* Alignment bar */}
                  <div className="space-y-2.5">
                    <div className="flex items-center gap-5">
                      <div className="flex-1 h-[4px] bg-warmGray/20 overflow-hidden rounded-full">
                        <div
                          className={`h-full ${res.bgClass} transition-all duration-1000 ease-out rounded-full`}
                          style={{ width: res.barWidth }}
                        ></div>
                      </div>
                      <span className={`text-[12px] font-sans font-semibold tabular-nums ${res.textClass}`}>
                        {pct}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-charcoal/25 text-[9px] font-sans">Exploratory</span>
                      <span className="text-charcoal/25 text-[9px] font-sans">Strong Alignment</span>
                    </div>
                  </div>
                </div>

                {/* Result body */}
                <div className="px-12 py-10 flex flex-col gap-8 border-t border-warmGray/15">

                  {/* Description */}
                  <p className="text-charcoal/65 text-[12px] font-sans font-light leading-[2]">
                    {res.description}
                  </p>

                  {/* Category breakdown */}
                  <div>
                    <p className="text-[9px] font-sans font-semibold tracking-[0.3em] uppercase text-charcoal/30 mb-6">
                      Response Summary
                    </p>
                    <div className="space-y-5">
                      {categoryBreakdown.map((cat, ci) => {
                        const catScore = cat.qs.reduce((acc, qi) => {
                          const ans = answers[qi];
                          return acc + (ans !== undefined ? selfAssessmentQuestions[qi].options[ans].score : 0);
                        }, 0);
                        const catMax = cat.qs.length * 3;
                        const catPct = Math.round((catScore / catMax) * 100);
                        return (
                          <div key={ci}>
                            <div className="flex items-center justify-between mb-2.5">
                              <span className="text-[10.5px] font-sans font-light text-charcoal/55">{cat.label}</span>
                              <span className={`text-[10px] font-sans font-semibold tabular-nums ${res.textClass}`}>{catPct}%</span>
                            </div>
                            <div className="h-[3px] bg-warmGray/15 overflow-hidden rounded-full">
                              <div
                                className={`h-full ${res.bgClass} rounded-full transition-all duration-700`}
                                style={{ width: `${catPct}%`, transitionDelay: `${ci * 100}ms` }}
                              ></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Recommendation */}
                  <div className="flex items-start gap-5 px-6 py-5 bg-cream/60 border-l-[3px] border-bronze/40">
                    <i className="ri-leaf-line text-bronze/60 text-base flex-shrink-0 mt-0.5"></i>
                    <p className="text-charcoal/60 text-[11px] font-sans font-light leading-[1.9] italic">
                      {res.note}
                    </p>
                  </div>

                  {/* CTAs */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={() => navigate('/clinical')}
                      className="group flex-1 flex items-center justify-between px-8 py-4 bg-charcoal text-white hover:bg-bronze transition-all duration-300 whitespace-nowrap cursor-pointer rounded-full"
                    >
                      <span className="text-[9px] font-sans font-semibold tracking-[0.25em] uppercase">Begin Your Stability</span>
                      <i className="ri-arrow-right-line text-xs group-hover:translate-x-0.5 transition-transform duration-300"></i>
                    </button>
                    <button
                      onClick={handleRestart}
                      className="flex items-center justify-center gap-2.5 px-7 py-4 border border-warmGray/30 text-charcoal/40 text-[9px] font-sans font-semibold tracking-[0.22em] uppercase hover:border-bronze/40 hover:text-bronze hover:bg-cream/30 transition-all duration-300 whitespace-nowrap cursor-pointer rounded-full"
                    >
                      <i className="ri-refresh-line text-xs"></i>
                      <span>Retake</span>
                    </button>
                  </div>

                  <p className="text-charcoal/20 text-[9px] font-sans font-light italic text-center">
                    For personal reflection only — not a clinical evaluation.
                  </p>
                </div>
              </div>
            )}

          </div>
        </div>

        {/* Bottom note */}
        <div className="mt-8 sm:mt-16 flex items-center gap-5">
          <div className="w-8 h-[1px] bg-bronze/40 flex-shrink-0"></div>
          <p className="text-charcoal/40 text-[10.5px] font-sans font-light italic leading-relaxed">
            You do not need to wait until exhaustion becomes visible.
          </p>
        </div>

      </div>

      {/* Bottom gradient divider */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-bronze/25 to-transparent"></div>
    </section>
  );
};

export default ClientSuitability;